import React from 'react';
import connectToStores from 'alt-utils/lib/connectToStores';
import TriggerRuleActions from 'actions/triggers/TriggerRuleActions';
import {List} from 'immutable';
import uniq from 'lodash/uniq';
import TriggerPermission from 'records/TriggerPermission';
import {Link} from 'react-router';
import './RulesTable.styl';
import BulletedRuleDescription from 'components/triggers/BulletedRuleDescription';
import {RuleTagsDialog} from 'components/triggers/rule-tags-dialog/RuleTagsDialog';
import RuleNotificationToggle from 'components/triggers/rules/RuleNotificationToggle';
import RuleTags from 'components/triggers/rules/RuleTags';
import UserActions from 'actions/UserActions';
import UserStore from 'stores/UserStore';
import RuleAvatar from 'components/triggers/rules/RuleAvatar';
import RulePermissionsDialog from 'components/triggers/rule-permissions-dialog';
import DefaultTriggerTagStore from 'stores/triggers/DefaultTriggerTagStore';
import DefaultTriggerTagActions from 'actions/triggers/DefaultTriggerTagActions';
import SmartAlertConfig from 'components/triggers/rules/SmartAlertConfig';

@connectToStores
export default class RulesTable extends React.PureComponent {
  constructor(props) {
    super(props);
    this.renderTableRow = this.renderTableRow.bind(this);
    this.toggleNotify = this.toggleNotify.bind(this);
    this.closeTagging = this.closeTagging.bind(this);

    this.state = {
      showTaggingForRuleId: null,
      showSharingForRuleId: null,
    };
    this.highlightedRef = null;
  }

  static getStores() {
    return [UserStore, DefaultTriggerTagStore];
  }

  static getPropsFromStores(nextProps) {
    const defaultTags = DefaultTriggerTagStore.getState().tags;

    const {rules} = nextProps;
    if (rules) {
      const ownerIds = uniq(rules.map(r => r.userId).toArray());
      const users = UserStore.getState().users;
      const owners = ownerIds.map(id => users.get(id));
      return {
        owners,
        defaultTags,
      };
    }
    return {
      owners: List(),
      defaultTags,
    };
  }

  async toggleNotify(rule, active) {
    await TriggerRuleActions.updateTriggerRulePermissions(
      rule.id,
      List([
        new TriggerPermission({
          permission: 'NOTIFY',
          active: !active,
          userId: this.props.sessionUser.id,
        }),
      ])
    );
    const newRules = this.props.rules.setIn(
      [rule.id.toString(), 'rulePermissions', 'NOTIFY'],
      !active
    );
    TriggerRuleActions.fetchTriggerRulesSuccess(newRules);
  }

  scrollToHighlighted() {
    window.scrollTo(0, this.highlightedRef.offsetTop);
  }

  componentDidUpdate() {
    if (this.highlightedRef) {
      this.scrollToHighlighted();
    }
  }

  componentDidMount() {
    const {rules} = this.props;

    const ownerIds = uniq(rules.map(r => r.userId).toArray());
    UserActions.fetchUsers.defer([ownerIds]);

    DefaultTriggerTagActions.fetchDefaultTriggerTags.defer();
  }

  getUniqueTags(rules) {
    // get all the unique tag names
    return uniq(
      rules
        .valueSeq()
        .flatMap(r => r.tags)
        .toArray()
        .map(tag => tag.name)
    );
  }

  getRulesWithTag(tagName, rules, searchText) {
    return rules.filter(
      rule =>
        !rule.antiRule &&
        (!searchText || new RegExp(searchText, 'i').test(rule.name)) &&
        rule.tags.findIndex(tag => tag.name === tagName) > -1
    );
  }

  renderTableRow(tagName, rule, owners, hasSharePermissions, highlighted) {
    const {sessionUser} = this.props;
    const owner = owners.find(o => o && o.id === rule.userId);
    const canEdit = rule.rulePermissions.EDIT !== undefined; // edit and delete
    const canShare = (rule.rulePermissions.SHARE !== undefined || canEdit) && hasSharePermissions;

    return (
      <tr
        key={rule.id}
        {...(highlighted === rule.id
          ? {
              ref: ref => (this.highlightedRef = ref),
              className: 'highlighted',
            }
          : {})}>
        <td className="notification-toggle">
          <RuleNotificationToggle rule={rule} tag={tagName} toggleNotify={this.toggleNotify} />
        </td>
        <td>
          <Link className="generic-link" to={`/admin/actions/${rule.id}`}>
            <BulletedRuleDescription text={rule.name} timing={rule.trendDescription} />
          </Link>
        </td>
        <td className="tags">
          <RuleTags tagNames={rule.tags.map(tag => tag.name)} />
        </td>
        <td>
          <RuleAvatar owner={owner} sessionUser={sessionUser} />
        </td>
        <td>
          <SmartAlertConfig
            sessionUser={sessionUser}
            rule={rule}
            canEdit={canEdit}
            canShare={canShare}
            setTaggingRule={id => this.setState({showTaggingForRuleId: id})}
            setSharingRule={id => this.setState({showSharingForRuleId: id})}
          />
        </td>
      </tr>
    );
  }

  closeTagging() {
    this.setState({showTaggingForRuleId: null});
    TriggerRuleActions.fetchTriggerRules();
  }

  render() {
    const {rules, owners, searchText, highlighted, defaultTags} = this.props;

    const taggingRuleId =
      this.state.showTaggingForRuleId && this.state.showTaggingForRuleId.toString();
    const taggingRule = rules.get(taggingRuleId);

    const sharingRuleId =
      this.state.showSharingForRuleId && this.state.showSharingForRuleId.toString();
    const sharingRule = rules.get(sharingRuleId);

    const ruleList = rules.valueSeq();
    const hasSharePermissions = this.props.sessionUser.canShareTriggers();
    const tagNames = this.getUniqueTags(rules);
    const rulesWithNoTags = ruleList.filter(rule => !rule.tags || rule.tags.size === 0).toArray();

    return (
      <table className="trigger-rules-table generic-table">
        <thead>
          <tr>
            <th className="notify">Notify me</th>
            <th className="trigger-description">Trigger</th>
            <th className="tags">Tags</th>
            <th className="owner">Owner</th>
            <th className="actions">Actions</th>
          </tr>
        </thead>
        <tbody>
          {tagNames.map(tag => [
            <tr key={tag}>
              <td />
              <td colSpan="4">{tag.toUpperCase()}</td>
            </tr>,
            this.getRulesWithTag(tag, ruleList, searchText).map(rule =>
              this.renderTableRow(tag, rule, owners, hasSharePermissions, highlighted)
            ),
          ])}
          {tagNames.length > 0 && rulesWithNoTags.length > 0 && (
            <tr>
              <td />
              <td colSpan="3">OTHER</td>
            </tr>
          )}
          {rulesWithNoTags.map(rule =>
            this.renderTableRow('Others', rule, owners, hasSharePermissions, highlighted)
          )}
          {taggingRule && (
            <RuleTagsDialog
              rule={taggingRule}
              defaultTags={defaultTags}
              onRequestClose={() => this.closeTagging()}
            />
          )}
          {sharingRule && (
            <RulePermissionsDialog
              rule={sharingRule}
              onSaveSuccess={() =>
                // reload the whole set of rules, as the rule that just had its permissions changed may no longer be
                // permissioned to this user
                TriggerRuleActions.fetchTriggerRules()
              }
              onRequestClose={() => this.setState({showSharingForRuleId: null})}
            />
          )}
        </tbody>
      </table>
    );
  }
}
